<script setup lang="ts">
/**
 * BranchSearch — branch autocomplete in the AppHeader center.
 *
 * First focus loads the FULL active-branch list once (GET /branches with
 * perPage 500 — server clamp; 58 branches today) and keeps it for the
 * session, so the empty-focus dropdown is a scrollable picker over every
 * branch. Typing filters that cached list client-side (substring match on
 * name_th / address_th / phone, mirroring the server's LIKE columns) —
 * no per-keystroke network traffic. Selecting a result navigates to
 * /branches/:id. Keyboard: ↑/↓ move, Enter select, Esc close.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { fetchBranches, type BranchSummary } from '@/api';
import BranchName from '@/components/BranchName.vue';

const router = useRouter();

// Server clamps perPage at 500 (branches.go maxPerPage) — same full-set
// fetch the map view uses. Every active branch fits one page.
const FETCH_PER_PAGE = 500;

const inputRef = ref<HTMLInputElement | null>(null);
const rootRef = ref<HTMLElement | null>(null);
const query = ref('');
const open = ref(false);
const loading = ref(false);
const allBranches = ref<BranchSummary[]>([]);
const activeIndex = ref(-1);

let loadController: AbortController | undefined;

/** Load the full branch list once per session; a failed load retries on next focus. */
async function loadAll() {
  if (loading.value || allBranches.value.length > 0) return;
  loadController?.abort();
  const controller = new AbortController();
  loadController = controller;
  loading.value = true;
  try {
    const res = await fetchBranches({ perPage: FETCH_PER_PAGE }, controller.signal);
    if (!controller.signal.aborted) allBranches.value = res.items;
  } catch {
    // Leave list empty — next focus retries.
  } finally {
    if (!controller.signal.aborted) loading.value = false;
  }
}

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return allBranches.value;
  return allBranches.value.filter(
    (b) =>
      b.name_th?.toLowerCase().includes(q) ||
      b.address_th?.toLowerCase().includes(q) ||
      (b.phone ?? '').toLowerCase().includes(q),
  );
});

// Filter set changed → the old highlight points at the wrong row.
watch(filtered, () => {
  activeIndex.value = -1;
});

function onOpen() {
  open.value = true;
  loadAll();
}

function close() {
  open.value = false;
  activeIndex.value = -1;
}

function onKeydown(ev: KeyboardEvent) {
  if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
    if (filtered.value.length === 0) return;
    ev.preventDefault();
    const delta = ev.key === 'ArrowDown' ? 1 : -1;
    activeIndex.value =
      (activeIndex.value + delta + filtered.value.length) % filtered.value.length;
  } else if (ev.key === 'Enter') {
    const idx = activeIndex.value >= 0 ? activeIndex.value : 0;
    if (filtered.value[idx]) {
      ev.preventDefault();
      select(filtered.value[idx]);
    }
  } else if (ev.key === 'Escape') {
    close();
    inputRef.value?.blur();
  }
}

function select(branch: BranchSummary) {
  close();
  query.value = '';
  inputRef.value?.blur();
  router.push({ name: 'branch', params: { id: branch.id } });
}

// Click-outside close (mousedown so it wins before sibling click handlers).
function onDocMousedown(ev: MouseEvent) {
  if (!rootRef.value?.contains(ev.target as Node)) close();
}

onMounted(() => document.addEventListener('mousedown', onDocMousedown));
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocMousedown);
  loadController?.abort();
});

const showDropdown = computed(
  () => open.value && (loading.value || allBranches.value.length > 0 || query.value.trim().length > 0),
);
const listboxId = 'branch-search-listbox';
const activeId = computed(() =>
  activeIndex.value >= 0 ? `${listboxId}-opt-${activeIndex.value}` : undefined,
);
</script>

<template>
  <div ref="rootRef" class="branch-search">
    <span class="branch-search__icon" aria-hidden="true">⌕</span>
    <input
      ref="inputRef"
      v-model="query"
      class="branch-search__input"
      type="search"
      placeholder="ค้นหาสาขา…"
      role="combobox"
      :aria-expanded="showDropdown"
      :aria-controls="listboxId"
      :aria-activedescendant="activeId"
      aria-autocomplete="list"
      autocomplete="off"
      @focus="onOpen"
      @keydown="onKeydown"
    />
    <ul
      v-if="showDropdown"
      :id="listboxId"
      class="branch-search__dropdown"
      role="listbox"
    >
      <li v-if="loading && allBranches.length === 0" class="branch-search__hint">
        กำลังโหลดรายชื่อสาขา…
      </li>
      <li v-else-if="filtered.length === 0" class="branch-search__hint">
        ไม่พบสาขา
      </li>
      <li
        v-for="(b, i) in filtered"
        :key="b.id"
        :id="`${listboxId}-opt-${i}`"
        class="branch-search__option"
        role="option"
        :aria-selected="i === activeIndex"
        :class="{ 'is-active': i === activeIndex }"
        @mousedown.prevent
        @click="select(b)"
        @mousemove="activeIndex = i"
      >
        <BranchName :name="b.name_th" :code="b.brand?.code" />
        <span class="branch-search__option-region">{{ b.region?.name_th }}</span>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.branch-search {
  position: relative;
  flex: 1 1 auto;
  max-width: 22rem;
  margin: 0 auto;
  min-width: 0;
}

.branch-search__icon {
  position: absolute;
  left: 0.55rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-dim);
  font-size: 0.95rem;
  line-height: 1;
  pointer-events: none;
}

.branch-search__input {
  width: 100%;
  border: 1px solid var(--line-light);
  border-radius: var(--radius-chip);
  background: var(--surface-2, transparent);
  padding: 0.4rem 0.7rem 0.4rem 1.7rem;
  font-size: 0.8rem;
  color: var(--text);
  font-family: inherit;
}

.branch-search__input:focus {
  outline: none;
  border-color: var(--brand-light);
  background: var(--surface);
}

/* Hide the native clear button — we reset on select/Esc ourselves. */
.branch-search__input::-webkit-search-cancel-button {
  -webkit-appearance: none;
}

.branch-search__dropdown {
  position: absolute;
  top: calc(100% + 0.35rem);
  left: 0;
  right: 0;
  list-style: none;
  margin: 0;
  padding: 0.25rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
  box-shadow: 0 0.5rem 1.25rem rgba(0, 0, 0, 0.25);
  max-height: 18rem;
  overflow-y: auto;
  z-index: 200;
}

.branch-search__hint {
  padding: 0.5rem 0.65rem;
  font-size: 0.78rem;
  color: var(--text-dim);
}

.branch-search__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.65rem;
  border-radius: calc(var(--radius-chip) - 2px);
  cursor: pointer;
  font-size: 0.82rem;
}

.branch-search__option.is-active {
  background: var(--surface-2);
}

.branch-search__option-region {
  color: var(--text-dim);
  font-size: 0.7rem;
  white-space: nowrap;
  flex-shrink: 0;
}
</style>
